import axios from 'axios';

// API base URL from environment or default - use relative URL in development
const API_URL = '';
console.log('Using relative API URL for development');

// Create axios instance with authentication
const api = axios.create({
  baseURL: API_URL
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Get all clinics
export const getClinics = async () => {
  try {
    const response = await api.get('/api/admin/clinics');
    return response.data;
  } catch (error) {
    console.error('Error fetching clinics:', error);
    throw error;
  }
};

// Get single clinic by ID
export const getClinicById = async (id) => {
  try {
    const response = await api.get(`/api/admin/clinics/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching clinic ${id}:`, error);
    throw error;
  }
};

// Create new clinic
export const createClinic = async (clinicData) => {
  try {
    const response = await api.post('/api/admin/clinics', clinicData);
    return response.data;
  } catch (error) {
    console.error('Error creating clinic:', error);
    throw error;
  }
};

// Update existing clinic
export const updateClinic = async (id, clinicData) => {
  try {
    const response = await api.put(`/api/admin/clinics/${id}`, clinicData);
    return response.data;
  } catch (error) {
    console.error(`Error updating clinic ${id}:`, error);
    throw error;
  }
};

// Get all appointment types
export const getAllAppointmentTypes = async () => {
  try {
    const response = await api.get('/api/appointment-types');
    return response.data;
  } catch (error) {
    console.error('Error fetching all appointment types:', error);
    throw error;
  }
};

// Get clinic appointment types
export const getClinicAppointmentTypes = async (clinicId) => {
  try {
    const response = await api.get(`/api/appointment-types/clinic/${clinicId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching appointment types for clinic ${clinicId}:`, error);
    throw error;
  }
};

// Update clinic appointment types
export const updateClinicAppointmentTypes = async (clinicId, appointmentTypes) => {
  try {
    console.log('Sending to API:', { appointmentTypes });
    const response = await api.put(`/api/appointment-types/clinic/${clinicId}`, {
      appointmentTypes: appointmentTypes // Making sure we're sending the array in the expected format
    });
    return response.data;
  } catch (error) {
    console.error(`Error updating appointment types for clinic ${clinicId}:`, error);
    // Log the actual response data if available
    if (error.response?.data) {
      console.error('Server response:', error.response.data);
    }
    throw error;
  }
};

// Sync appointment types from Cliniko
export const syncAppointmentTypes = async () => {
  try {
    const response = await api.get('/api/appointment-types/sync');
    return response.data;
  } catch (error) {
    console.error('Error syncing appointment types:', error);
    throw error;
  }
};

// Delete clinic
export const deleteClinic = async (id) => {
  try {
    const response = await api.delete(`/api/admin/clinics/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting clinic ${id}:`, error);
    throw error;
  }
};

// Authentication: Login
export const login = async (credentials) => {
  try {
    console.log('Login attempt with:', credentials);
    const response = await axios.post(`${API_URL}/api/auth/login`, credentials);
    console.log('Login response:', response.data);
    
    // Store token in localStorage
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      console.log('Saved token and user to localStorage');
    } else {
      console.warn('No token received in login response');
    }
    
    return response.data;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

// Authentication: Logout
export const logout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
};

// Authentication: Get current user
export const getCurrentUser = async () => {
  try {
    const response = await api.get('/api/auth/me');
    return response.data;
  } catch (error) {
    console.error('Error getting current user:', error);
    throw error;
  }
};

// Authentication: Check if user is logged in
export const isLoggedIn = () => {
  return !!localStorage.getItem('token');
};

// Authentication: Register a new user (admin only)
export const registerUser = async (userData) => {
  try {
    const response = await api.post('/api/auth/register', userData);
    return response.data;
  } catch (error) {
    console.error('Error registering user:', error);
    throw error;
  }
};

// Get practitioner details from Cliniko
export const getPractitionerDetails = async (practitionerId) => {
  try {
    const response = await api.get(`/api/admin/practitioners/${practitionerId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching practitioner ${practitionerId}:`, error);
    throw error;
  }
};

// Update practitioner photo URL
export const updatePractitionerPhoto = async (practitionerId, photoData) => {
  try {
    const response = await api.put(`/api/admin/practitioners/${practitionerId}/photo`, photoData);
    return response.data;
  } catch (error) {
    console.error(`Error updating photo for practitioner ${practitionerId}:`, error);
    throw error;
  }
};

// Get practitioners for a clinic
export const getClinicPractitioners = async (clinicId) => {
  try {
    const response = await api.get(`/api/admin/clinics/${clinicId}/practitioners`);
    return response;
  } catch (error) {
    console.error(`Error fetching practitioners for clinic ${clinicId}:`, error);
    throw error;
  }
};

// Get all practitioners for a clinic
export const getPractitioners = async (clinicId) => {
  try {
    const response = await api.get(`/api/practitioners?clinic_id=${clinicId}`);
    return response.data.practitioners;
  } catch (error) {
    console.error(`Error fetching practitioners for clinic ${clinicId}:`, error);
    throw error;
  }
};

// Get appointment types assigned to a practitioner
export const getPractitionerAppointmentTypes = async (practitionerId, clinicId) => {
  try {
    console.log(`Fetching appointment types for practitioner ${practitionerId} and clinic ${clinicId}`);
    const response = await api.get(`/api/admin/practitioners/${practitionerId}/appointment-types?clinic_id=${clinicId}`);
    console.log('Response:', response.data);
    
    // Check if response contains the expected data
    if (response.data && Array.isArray(response.data.appointment_types)) {
      return response.data.appointment_types;
    } else if (response.data && Array.isArray(response.data)) {
      // Handle case where the API might return an array directly
      return response.data;
    } else {
      // Default to empty array if the data doesn't match expected format
      console.warn('Unexpected response format from appointment types API:', response.data);
      return [];
    }
  } catch (error) {
    console.error(`Error fetching appointment types for practitioner ${practitionerId}:`, error);
    // Return empty array instead of throwing to prevent component from breaking
    return [];
  }
};

// Assign appointment types to a practitioner
export const assignAppointmentTypesToPractitioner = async (practitionerId, clinicId, appointmentTypeIds) => {
  try {
    const response = await api.post(`/api/admin/practitioners/${practitionerId}/appointment-types`, {
      clinic_id: clinicId,
      appointment_type_ids: appointmentTypeIds
    });
    return response.data;
  } catch (error) {
    console.error(`Error assigning appointment types to practitioner ${practitionerId}:`, error);
    throw error;
  }
};

// Get practitioners assigned to an appointment type
export const getPractitionersForAppointmentType = async (appointmentTypeId, clinicId) => {
  try {
    const response = await api.get(`/api/admin/practitioners/appointment-types/${appointmentTypeId}/practitioners?clinic_id=${clinicId}`);
    return response.data.practitioners;
  } catch (error) {
    console.error(`Error fetching practitioners for appointment type ${appointmentTypeId}:`, error);
    throw error;
  }
};

// User Management Functions

// Get all users
export const getUsers = async () => {
  try {
    const response = await api.get('/api/admin/users');
    return response.data;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

// Get user by ID
export const getUserById = async (id) => {
  try {
    const response = await api.get(`/api/admin/users/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching user ${id}:`, error);
    throw error;
  }
};

// Create new user
export const createUser = async (userData) => {
  try {
    const response = await api.post('/api/admin/users', userData);
    return response.data;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

// Update existing user
export const updateUser = async (id, userData) => {
  try {
    const response = await api.put(`/api/admin/users/${id}`, userData);
    return response.data;
  } catch (error) {
    console.error(`Error updating user ${id}:`, error);
    throw error;
  }
};

// Delete (deactivate) user
export const deleteUser = async (id) => {
  try {
    const response = await api.delete(`/api/admin/users/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting user ${id}:`, error);
    throw error;
  }
};

// Restore deactivated user
export const restoreUser = async (id) => {
  try {
    const response = await api.post(`/api/admin/users/${id}/restore`);
    return response.data;
  } catch (error) {
    console.error(`Error restoring user ${id}:`, error);
    throw error;
  }
};